import { Button, Alert, Typography } from 'antd'
import { useState } from 'react'
import { AiOutlineLock, AiOutlineCheck } from 'react-icons/ai'
import { useParams, useNavigate } from 'react-router'

import { restClient } from '@/Core/Services/RestClientService'
import { Form, FormField } from '@/Core/UI/Form'
import { createFormContext } from '@/Core/UI/Form/Providers/FormContextProvider'

import { FormWrapper } from '../Components/FormWrapper'

const { Title, Text } = Typography

// Core's createFormContext is typed as `any`, so the submit payload is typed locally.
interface InvitationFormContext {
    // eslint-disable-next-line no-unused-vars -- false positive: ESLint core rule flags param names in function-type signatures
    setError?: (path: string, error: string) => void
}

interface InvitationSubmitPayload {
    values: Record<string, string>
    formContext?: InvitationFormContext
}

export default function UserInvitationPage() {
    document.title = 'Прийняти запрошення'

    const { token } = useParams()
    const navigate = useNavigate()

    const [isLoading, setIsLoading] = useState(false)
    const [isSuccess, setIsSuccess] = useState(false)
    const [error, setError] = useState<string | null>(null)

    const formContext = (() => {
        return createFormContext({
            fields: {
                code: {
                    label: 'Код підтвердження (з email)',
                    isRequired: true,
                },
                plainPassword: {
                    label: 'Новий пароль',
                    type: 'password',
                    isRequired: true,
                },
                confirmPassword: {
                    label: 'Підтвердіть пароль',
                    type: 'password',
                    isRequired: true,
                },
            },
            onSubmit: async ({ values, formContext: ctx }: InvitationSubmitPayload) => {
                if (values['plainPassword'] !== values['confirmPassword']) {
                    const msg = 'Паролі не співпадають'
                    setError(msg)
                    ctx?.setError?.('confirmPassword', msg)
                    return
                }

                setIsLoading(true)
                setError(null)

                try {
                    await restClient.patch(`/services/users/${token}/accept_invitation`, {
                        json: {
                            code: Number(values['code']),
                            plainPassword: values['plainPassword'],
                        },
                    })

                    setIsSuccess(true)
                } catch (err) {
                    interface ErrorViolation {
                        field: string
                        message: string
                    }
                    interface ErrorBody {
                        message?: string
                        violations?: ErrorViolation[]
                    }

                    let errorBody: ErrorBody | null = null
                    try {
                        const response = (err as { response?: { json?: () => Promise<ErrorBody> } }).response
                        if (typeof response?.json === 'function') {
                            errorBody = await response.json()
                        }
                    } catch {
                        // ignore
                    }

                    const msg = errorBody?.message || 'Помилка. Перевірте код підтвердження та спробуйте ще раз.'
                    setError(msg)

                    if (errorBody?.violations) {
                        errorBody.violations.forEach((v) => ctx?.setError?.(v.field, v.message))
                    }
                } finally {
                    setIsLoading(false)
                }
            },
        })
    })()

    if (isSuccess) {
        return (
            <FormWrapper showLogo={true}>
                <div style={{ textAlign: 'center', padding: '16px 0' }}>
                    <AiOutlineCheck style={{ fontSize: 48, color: '#52c41a', marginBottom: 16 }} />
                    <Title level={4} style={{ color: '#fff', margin: '0 0 8px' }}>
                        Запрошення прийнято!
                    </Title>
                    <Text style={{ color: 'rgba(255,255,255,0.7)', display: 'block', marginBottom: 24 }}>
                        Ваш пароль встановлено. Тепер ви можете увійти до системи.
                    </Text>
                    <Button type="primary" onClick={() => navigate('/')}>
                        Увійти
                    </Button>
                </div>
            </FormWrapper>
        )
    }

    return (
        <FormWrapper showLogo={true}>
            <div style={{ marginBottom: 16 }}>
                <Title level={4} style={{ color: '#fff', margin: '0 0 4px' }}>
                    <AiOutlineLock style={{ marginRight: 8 }} />
                    Встановити пароль
                </Title>
                <Text style={{ color: 'rgba(255,255,255,0.6)' }}>
                    Введіть код із листа та встановіть пароль для входу
                </Text>
            </div>

            {error && <Alert message={error} type="error" showIcon style={{ marginBottom: 16 }} />}

            <Form formContext={formContext}>
                <FormField name="code" />
                <FormField name="plainPassword" />
                <FormField name="confirmPassword" />
            </Form>

            <Button
                type="primary"
                style={{ width: '100%', marginTop: 8 }}
                loading={isLoading}
                onClick={() => formContext?.submit?.()}
            >
                Прийняти запрошення
            </Button>
        </FormWrapper>
    )
}

export const pageConfig = {
    route: '/user-invitation/:token',
    template: 'public',
}
